/**
 * Typed client for FormsHandler.ashx.
 *
 * This is the only frontend module that should know the FormsHandler action
 * names and legacy payload shapes. Designer components work with the React
 * schema; encode/decode adapters translate at this boundary.
 */
import type {
  DynamicFormDefinition,
  FilledFormCapacity,
  FormElementItem,
  FormListItem,
  FormListQuery,
  FormSelectItem,
  FormValues,
  LegacyPostedForm
} from '../models/form';
import { decodeLegacyForm, encodeFormForLegacyBackend } from '../legacy/schema-codec';
import { actionUrl, request } from './http';
import { readRuntimeUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';
import { normalizeFormElementsResponse } from '../utils/form-elements';

export async function getFormsList(query: FormListQuery, signal?: AbortSignal): Promise<FormListItem[]> {
  return request<FormListItem[]>(actionUrl('FormsHandler', 'GetFormsList'), { method: 'POST', body: query, signal });
}

export async function getFormsForSelect(): Promise<FormSelectItem[]> {
  return request<FormSelectItem[]>(actionUrl('FormsHandler', 'GetFormsForSelect'), { method: 'POST' });
}

export async function getFormElements(formId: number): Promise<FormElementItem[]> {
  const response = await request<unknown>(actionUrl('FormsHandler', 'GetFormElements', { FormId: formId }), { method: 'POST' });
  return normalizeFormElementsResponse(response);
}

/** Loads the legacy backend DTO and converts it to the canonical React schema. */
export async function getEditableForm(formId: number): Promise<DynamicFormDefinition> {
  const legacy = await request<LegacyPostedForm>(actionUrl('FormsHandler', 'GetEditableForm', { Id: formId }), { method: 'POST' });
  return decodeLegacyForm(legacy, formId);
}

/** Encodes a React definition into the backend-compatible DTO and returns the new numeric id. */
export async function createForm(form: DynamicFormDefinition): Promise<number> {
  const payload = encodeFormForLegacyBackend(form);
  const response = await request<number | string>(actionUrl('FormsHandler', 'InsertForm'), { method: 'POST', body: payload });
  const id = typeof response === 'number' ? response : Number(String(response).replaceAll('"', ''));
  if (!Number.isFinite(id) || id <= 0) throw new Error(publicCopy(readRuntimeUiLocale()).invalidCreatedFormId(response));
  return id;
}

/** Persists a complete form definition; the backend replaces tabs/elements transactionally where supported. */
export async function updateForm(form: DynamicFormDefinition): Promise<boolean> {
  const response = await request<boolean | string>(actionUrl('FormsHandler', 'EditForm'), { method: 'POST', body: encodeFormForLegacyBackend(form) });
  return response === true || response === 'true';
}

export async function deleteForm(formId: number): Promise<void> {
  await request(actionUrl('FormsHandler', 'DeleteForm', { FormId: formId }), { method: 'POST' });
}

export async function cloneForm(formId: number): Promise<void> {
  await request(actionUrl('FormsHandler', 'CloneForm', { FormId: formId }), { method: 'POST' });
}

/** Creates or updates a submission. A non-zero submittedFormId switches the backend to edit mode. */
export async function submitForm(formId: number, values: FormValues, submittedFormId = 0, moduleId = 0): Promise<string> {
  const response = await request<string>(actionUrl('FormsHandler', 'SubmitForm'), {
    method: 'POST',
    body: { FormId: formId, FormData: JSON.stringify(values), SubmittedFormId: String(submittedFormId), ModuleId: moduleId }
  });
  return String(response ?? '').replace(/^"|"$/g, '');
}

export async function getSubmittedFormData(submittedFormId: number): Promise<FormValues> {
  return request<FormValues>(actionUrl('FormsHandler', 'GetSubmittedFormData', { submittedFormId }), { method: 'POST' });
}

export async function getFormsDataList(formIDs: number[]): Promise<FormValues[]> {
  return request<FormValues[]>(actionUrl('FormsHandler', 'GetFormsDataList'), {
    method: 'POST',
    body: { formIDs: formIDs.join(',') }
  });
}

export async function getFilledFormsCount(formId: number): Promise<FilledFormCapacity> {
  return request<FilledFormCapacity>(actionUrl('FormsHandler', 'GetFilledFormsCount_Dynamic', { Id: formId }), { method: 'POST' });
}

export async function removeSubmittedForm(submittedFormId: number): Promise<void> {
  await request(actionUrl('FormsHandler', 'RemoveSubmittedForm', { Id: submittedFormId }), { method: 'POST' });
}

/** Uploads a respondent file; the server returns the stored safe file name. */
export async function uploadFile(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  return request<string>(actionUrl('FormsHandler', 'UploadFile'), { method: 'POST', body, responseType: 'text' });
}

/** Uploads an image referenced by a designer image field. */
export async function uploadDesignerImage(file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  const response = await request<string>(actionUrl('FormsHandler', 'ImageElementUpload'), { method: 'POST', body, responseType: 'text' });
  return String(response).replace(/^\"|\"$/g, '');
}

/** Calls the server-side uniqueness check and excludes the current submission during edits. */
export async function validateUnique(formId: number, model: string, value: string, submittedFormId = 0): Promise<boolean> {
  const response = await request<boolean | string>(actionUrl('FormsHandler', 'UniqueValidation', { FormId: formId, Model: model, Value: value, SubmittedFormId: submittedFormId }));
  return response === true || response === 'true';
}

/** Invokes an administrator-configured validation endpoint with the standard form/model/value query contract. */
export async function validateRemote(url: string, formId: number, model: string, value: string): Promise<boolean> {
  const separator = url.includes('?') ? '&' : '?';
  const response = await request<boolean | string>(`${url}${separator}${new URLSearchParams({ FormId: String(formId), Model: model, Value: value })}`);
  return response === true || response === 'true';
}

export async function validateCaptcha(value: string, challengeId?: string): Promise<boolean> {
  const response = await request<boolean | string>(actionUrl('FormsHandler', 'CaptchaValidation', {
    Value: value,
    ChallengeId: challengeId ?? ''
  }));
  return response === true || response === 'true';
}

export interface SimpleCaptchaRequest {
  challengeId: string;
  length?: number;
  mode?: 'digits' | 'math' | 'mixed' | 'letters';
  digitStyle?: 'persian' | 'latin';
  visualStyle?: 'easy' | 'normal';
}

export function getCaptchaUrl(options: SimpleCaptchaRequest): string {
  return actionUrl('FormsHandler', 'GenerateCaptcha', {
    ChallengeId: options.challengeId,
    Length: options.length ?? 4,
    Mode: options.mode ?? 'digits',
    DigitStyle: options.digitStyle ?? 'persian',
    VisualStyle: options.visualStyle ?? 'easy',
    rnd: Math.random()
  });
}

export interface SiteUiSettings {
  BootstrapVersion: '3' | '5';
  PortalId: number;
}

export async function getSiteUiSettings(): Promise<SiteUiSettings> {
  return request<SiteUiSettings>(actionUrl('FormsHandler', 'GetSiteUiSettings'), { method: 'POST' });
}

export async function updateSiteUiSettings(bootstrapVersion: '3' | '5'): Promise<SiteUiSettings> {
  return request<SiteUiSettings>(actionUrl('FormsHandler', 'UpdateSiteUiSettings'), {
    method: 'POST',
    body: { BootstrapVersion: bootstrapVersion }
  });
}
